import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AUTH_KEY = process.env.REACT_APP_AUTH_KEY;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': AUTH_KEY,
  },
});

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('Kresus-KPMG-auth-v1rf567');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login on unauthorized
      localStorage.removeItem('Kresus-KPMG-auth-v1rf567');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  access_token: string;
}

export interface UserProfile {
  email: string;
  password: string;
  role: string;
  walletAddress: string | null;
  firstName: string;
  lastName: string;
  verificationStatus: boolean | null;
  merkleRoot: string | null;
  nullifierHash: string | null;
  proof: string | null;
  verificationLevel: string | null;
}

export interface UserProfileResponse {
  user: UserProfile;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
}

export interface UpdateUserResponse {
  message: string;
}

export interface WhitelistedUser {
  email: string;
}

export interface WhitelistedUsersResponse {
  users: WhitelistedUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface SearchWhitelistedUsersResponse {
  results: WhitelistedUser[];
  count: number;
}

export interface WhitelistPaginationRequest {
  page: number;
  limit: number;
}

export interface AddWhitelistUserRequest {
  email: string;
}

export interface DeleteWhitelistUserRequest {
  email: string;
}

export interface SearchWhitelistUserRequest {
  input: string;
}

// Azure User Interfaces
export interface AzureUser {
  firstName: string;
  lastName: string;
  displayName: string;
}

export interface AzureUsersResponse {
  members: AzureUser[];
  skipToken: string | null;
  total: number;
  pageSize: number;
}

export interface AzureUsersRequest {
  pageSize: number;
  skipToken?: string;
}

export interface SearchAzureUserRequest {
  input: string;
}

export interface SearchAzureUserResponse {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
}

export interface AddAzureUserRequest {
  email: string;
  firstName: string;
  lastName: string;
}

export interface UpdateAzureUserRequest {
  email: string;
  firstName: string;
  lastName: string;
}

export interface DeleteAzureUserRequest {
  email: string;
}

// Domain Interfaces
export interface DomainMember {
  id: string;
  displayName: string;
  createdAt: string;
}

export interface AzureDomainsResponse {
  members: DomainMember[];
  skipToken: string;
  total: number;
}

export interface AzureDomainsRequest {
  pageSize: number;
  skipToken?: string;
}

export interface SearchDomainRequest {
  input: string;
}

export interface SearchDomainResponse {
  id: string;
  displayName: string;
}

// Legacy Domain Interfaces (keeping for compatibility)
export interface DomainStatus {
  id: string;
  displayName: string;
  createdAt: string;
}

export interface DomainStatusesResponse {
  domains: DomainStatus[];
}

export interface DomainActionRequest {
  domain: string;
}

export interface DomainActionResponse {
  message: string;
}

export interface TXTRecord {
  "@odata.type": string;
  id: string;
  isOptional: boolean;
  label: string;
  recordType: string;
  supportedService: string;
  ttl: number;
  text: string;
}

export interface TXTRecordRequest {
  domain: string;
}

// Password Reset Interfaces
export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
}

export interface VerifyCodeResponse {
  message: string;
}

export interface ResendCodeRequest {
  email: string;
}

export interface ResendCodeResponse {
  message: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface MismatchedUser {
  id: number;
  resolved: boolean;
  email: string;
  firstName: string | null;
  lastName: string | null;
  oldNullifierHash: string;
  newNullifierHash: string;
}

export interface MismatchedUsersResponse {
  verificationMissMatchesData: MismatchedUser[];
}

export interface ResetWorldIdVerificationRequest {
  id: string;
  email: string;
}

export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/v2/user/adminLogin', credentials);
    return response.data;
  },
  getUserProfile: async (): Promise<UserProfileResponse> => {
    const response = await apiClient.get<UserProfileResponse>('/v2/user');
    return response.data;
  },
  updateUser: async (userData: UpdateUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.patch<UpdateUserResponse>('/v2/user/updateUser', userData);
    return response.data;
  },
  getWhitelistedUsers: async (pagination: WhitelistPaginationRequest): Promise<WhitelistedUsersResponse> => {
    const response = await apiClient.post<WhitelistedUsersResponse>('/v2/user/whitelistedUsers', pagination);
    return response.data;
  },
  addWhitelistUser: async (userData: AddWhitelistUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.post<UpdateUserResponse>('/v2/user/addNewDBWhitelistUser', userData);
    return response.data;
  },
  deleteWhitelistUser: async (userData: DeleteWhitelistUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.post<UpdateUserResponse>('/v2/user/deleteDBWhitelistedUser', userData);
    return response.data;
  },
  searchWhitelistUser: async (searchData: SearchWhitelistUserRequest): Promise<SearchWhitelistedUsersResponse> => {
    const response = await apiClient.post<SearchWhitelistedUsersResponse>('/v2/user/searchDBWhitelistedUser', searchData);
    return response.data;
  },
  // Azure User APIs
  getAzureUsers: async (request: AzureUsersRequest): Promise<AzureUsersResponse> => {
    const response = await apiClient.post<AzureUsersResponse>('/v2/user/azureGroup', request);
    return response.data;
  },
  searchAzureUser: async (searchData: SearchAzureUserRequest): Promise<SearchAzureUserResponse[]> => {
    const response = await apiClient.post<SearchAzureUserResponse[]>('/v2/user/searchAzureUser', searchData);
    return response.data;
  },
  addAzureUser: async (userData: AddAzureUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.post<UpdateUserResponse>('/v2/user/addNewAzureUser', userData);
    return response.data;
  },
  updateAzureUser: async (userData: UpdateAzureUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.patch<UpdateUserResponse>('/v2/user/updateAzureUser', userData);
    return response.data;
  },
  deleteAzureUser: async (userData: DeleteAzureUserRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.post<UpdateUserResponse>('/v2/user/deleteAzureUser', userData);
    return response.data;
  },
  // Domain APIs
  getDomainStatuses: async (requestData: AzureDomainsRequest = { pageSize: 10 }): Promise<AzureDomainsResponse> => {
    const response = await apiClient.post<AzureDomainsResponse>('/v2/user/azureDomains', requestData);
    return response.data;
  },
  searchDomain: async (searchData: SearchDomainRequest): Promise<SearchDomainResponse[]> => {
    const response = await apiClient.post<SearchDomainResponse[]>('/v2/user/searchDomain', searchData);
    return response.data;
  },
  addCustomDomain: async (domainData: DomainActionRequest): Promise<DomainActionResponse> => {
    const response = await apiClient.post<DomainActionResponse>('/v2/user/whitelistDomain', domainData);
    return response.data;
  },
  deleteCustomDomain: async (domainData: DomainActionRequest): Promise<DomainActionResponse> => {
    const response = await apiClient.post<DomainActionResponse>('/v2/user/deleteDomain', domainData);
    return response.data;
  },
  verifyDomain: async (domainData: DomainActionRequest): Promise<DomainActionResponse> => {
    const response = await apiClient.post<DomainActionResponse>('/v2/user/verifyDomain', domainData);
    return response.data;
  },
  getTXTRecord: async (domainData: TXTRecordRequest): Promise<TXTRecord[]> => {
    const response = await apiClient.post<TXTRecord[]>('/v2/user/TXTRecord', domainData);
    return response.data;
  },
  // Password Reset APIs
  forgotPassword: async (request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post<ForgotPasswordResponse>('/v2/user/forgotPassword', request);
    return response.data;
  },
  verifyCode: async (request: VerifyCodeRequest): Promise<VerifyCodeResponse> => {
    const response = await apiClient.post<VerifyCodeResponse>('/v2/user/verifyCode', request);
    return response.data;
  },
  resendCode: async (request: ResendCodeRequest): Promise<ResendCodeResponse> => {
    const response = await apiClient.post<ResendCodeResponse>('/v2/user/resendCode', request);
    return response.data;
  },
  resetPassword: async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post<ResetPasswordResponse>('/v2/user/resetPassword', request);
    return response.data;
  },
  getMismatchedUsers: async (): Promise<MismatchedUsersResponse> => {
    const response = await apiClient.get<MismatchedUsersResponse>('/v2/world-id/listOfMissMatched');
    return response.data;
  },
  resetUserWorldIdVerification: async (request: ResetWorldIdVerificationRequest): Promise<UpdateUserResponse> => {
    const response = await apiClient.post<UpdateUserResponse>('/v2/world-id/resetUserWorldIdVerification', request);
    return response.data;
  },
};

export default apiClient;
